import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  ChevronDown,
  Settings,
  LogOut,
  Sun,
  Moon,
  Building2,
  Layers,
  Check,
  Loader2,
  ArrowRightLeft,
  HelpCircle,
  RefreshCw,
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { useTheme } from '../../contexts/ThemeContext';
import { useAuth } from '../../contexts/AuthContext';
import { useView } from '../../contexts/ViewContext';
import { usePortalMode } from '../../hooks/usePortalMode';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { Button } from '../ui/button';
import { NotificationBellPopover } from '../notifications';
import { useHelp } from '../help';
import { usePageRefresh } from '../../contexts/PageRefreshContext';
import { useMenuSettings } from '../../contexts/MenuSettingsContext';
import GlobalSearch from './GlobalSearch';

export default function TopBar() {
  // Notification bell placement is admin-configurable (top bar / sidebar / both).
  const { notificationIconTop } = useMenuSettings();
  const { theme, toggleTheme } = useTheme();
  const { user, logout } = useAuth();

  // "Guest" is what an anonymous visitor is called, so showing it to someone
  // who has signed in reads as a broken session — and it happens to every user
  // whose record carries no username and no full name, which is every account
  // created by an import rather than by hand. Their email always identifies
  // them, so it stands in before the anonymous label does.
  const displayName = user?.username || user?.full_name || user?.email || 'Guest';
  const displayInitials = displayName === 'Guest'
    ? 'G'
    : displayName.split(/[\s@._-]+/).filter(Boolean).slice(0, 2)
        .map((part: string) => part[0].toUpperCase()).join('');
  const navigate = useNavigate();

  // Portal mode for badge/label display
  const { portalMode } = usePortalMode();

  // Help context
  const { openPageHelp, openGlossary } = useHelp();

  // Page refresh — re-fetches current page data without navigating away
  const { isRefreshing, refresh } = usePageRefresh();

  // View context for company/site switching
  const {
    viewLevel,
    selectedGroupId,
    selectedGroupName,
    selectedCompanyName,
    selectedSiteName,
    supportsDualMode,
    isSaving,
    accessibleSites,
    accessibleCompanies,
    companyGroup,
    selectGroup,
    selectSite,
    selectCompany,
    togglePortalMode,
    hasSiteRestrictions,
  } = useView();

  // "View Data For" can list dozens of companies for a large group — filter
  // text plus a scroll cap keep the menu usable. Retired (inactive) companies
  // are hidden from the switcher entirely.
  const [viewQuery, setViewQuery] = useState('');
  const viewQ = viewQuery.trim().toLowerCase();
  const switcherCompanies = accessibleCompanies.filter((c) =>
    ((c as unknown as Record<string, unknown>)['is_active'] !== false)
    && (!viewQ || c.name.toLowerCase().includes(viewQ)));
  const switcherSites = accessibleSites.filter((s) =>
    ((s as unknown as Record<string, unknown>)['is_active'] !== false)
    && (!viewQ || s.name.toLowerCase().includes(viewQ)));

  const handleLogout = () => {
    // Use AuthContext logout which clears state, localStorage, and calls API
    logout();
    // Redirect to login page
    navigate('/login');
  };

  return (
    <header data-tour="topbar" className="sticky top-0 z-30 bg-white dark:bg-card border-b border-gray-200 dark:border-border" role="banner">
      <div className="px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Left section - Search (CR-3954) */}
          <div className="flex-1 flex items-center pl-12 md:pl-0">
            <GlobalSearch />
          </div>

          {/* Right section */}
          <div className="ml-4 flex items-center space-x-2">
            {/* Refresh — re-fetches current page data in place */}
            <Button
              variant="ghost"
              size="icon"
              onClick={refresh}
              disabled={isRefreshing}
              aria-label="Refresh page data"
              title="Refresh"
              className="hover:bg-gray-100 dark:hover:bg-accent"
            >
              <RefreshCw className={`h-5 w-5 text-gray-600 dark:text-muted-foreground ${isRefreshing ? 'animate-spin' : ''}`} aria-hidden="true" />
            </Button>

            {/* Theme toggle */}
            <Button
              variant="ghost"
              size="icon"
              onClick={toggleTheme}
              aria-label={`Switch to ${theme === 'light' ? 'dark' : 'light'} mode`}
              className="hover:bg-gray-100 dark:hover:bg-accent"
            >
              {theme === 'light' ? (
                <Moon className="h-5 w-5 text-gray-600 dark:text-muted-foreground" aria-hidden="true" />
              ) : (
                <Sun className="h-5 w-5 text-gray-600 dark:text-muted-foreground" aria-hidden="true" />
              )}
            </Button>


            {/* Help button */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="hover:bg-gray-100 dark:hover:bg-accent"
                  aria-label="Help"
                >
                  <HelpCircle className="h-5 w-5 text-gray-600 dark:text-muted-foreground" aria-hidden="true" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <DropdownMenuItem onClick={openPageHelp} className="cursor-pointer">
                  Page Help
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => openGlossary()} className="cursor-pointer">
                  Glossary
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Notifications — hidden in the top bar when admin has moved it to the sidebar only. */}
            {notificationIconTop && <NotificationBellPopover />}

            {/* User menu */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  className="flex items-center hover:bg-gray-100 dark:hover:bg-accent"
                  aria-label="User menu"
                >
                  <span
                    className="hidden sm:inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold mr-2 border bg-white text-gray-900 border-gray-300 dark:bg-gray-800 dark:text-gray-100 dark:border-gray-600"
                  >
                    {portalMode === 'supplier' ? 'Supplier' : 'Customer'}
                  </span>
                  <div className="h-8 w-8 rounded-full bg-blue-600 dark:bg-primary flex items-center justify-center text-white dark:text-primary-foreground font-medium mr-2">
                    {displayInitials}
                  </div>
                  <div className="hidden md:flex flex-col items-start mr-1">
                    <span className="text-gray-700 dark:text-foreground font-medium text-sm leading-tight">{displayName}</span>
                    <span className="text-xs text-muted-foreground leading-tight">
                      {viewLevel === 'GROUP' && selectedGroupName ? (
                        <>{selectedGroupName}</>
                      ) : viewLevel === 'SITE' && selectedSiteName ? (
                        <>{selectedSiteName}</>
                      ) : selectedCompanyName ? (
                        <>{selectedCompanyName}</>
                      ) : user?.companyName ? (
                        <>{user.companyName}</>
                      ) : (
                        'No context'
                      )}
                    </span>
                  </div>
                  <ChevronDown className="ml-1 h-4 w-4 text-gray-500 dark:text-muted-foreground" aria-hidden="true" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-72">
                <DropdownMenuLabel>
                  <div>
                    <p className="text-sm font-medium text-gray-900 dark:text-foreground">{displayName}</p>
                    <p className="text-xs text-gray-600 dark:text-muted-foreground">{user?.email || ''}</p>
                  </div>
                </DropdownMenuLabel>

                {/* Data Context Switcher - hierarchical view selection */}
                {(switcherCompanies.length > 0 || companyGroup || switcherSites.length > 0) && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuLabel data-tour="view-selector" className="flex items-center justify-between">
                      <span className="text-xs uppercase tracking-wide text-muted-foreground">
                        View Data For
                      </span>
                      {isSaving && <Loader2 className="h-3 w-3 animate-spin text-muted-foreground" />}
                    </DropdownMenuLabel>
                    {(accessibleCompanies.length > 10 || accessibleSites.length > 10) && (
                      <div className="px-2 pb-1">
                        <input
                          type="text"
                          value={viewQuery}
                          placeholder="Search companies…"
                          onChange={(e) => setViewQuery(e.target.value)}
                          onKeyDown={(e) => e.stopPropagation()}
                          className="w-full rounded-md border border-input bg-background px-2 py-1.5 text-sm outline-none focus:ring-1 focus:ring-ring"
                        />
                      </div>
                    )}
                    <div className="max-h-[55vh] overflow-y-auto">

                    {/* CR-6086: Top-group (whole-org) rollup entry. When the
                        acting user's accessible sites/companies span more than
                        one division under a common ancestor, the per-division
                        headers below each select a single division — there is
                        no whole-organisation entry, so a group-scoped admin can
                        never roll up across all divisions at once. companyGroup
                        is the user's HOME division (a sub-group); its
                        parent_group is that common ancestor (the root
                        CompanyGroup, e.g. "Acme Industrial Systems"). Offer it
                        as a selectable rollup ABOVE the divisions.

                        Backend already supports the selection:
                        core/serializers/user.py validate_current_group accepts
                        an ancestor group reachable via the user's accessible
                        sites (get_ancestors), and user_views.get_queryset
                        resolves it with current_group.get_all_companies()
                        (recursive) for the full cross-division list. Shown only
                        when (a) the home group has a parent (it's a sub-group —
                        super admins' synthetic "All Companies" has none and
                        keeps its existing entry) and (b) sites span >1 division
                        (single-division users are unchanged — AC3). */}
                    {(() => {
                      if (!companyGroup) return null;
                      const rootGroupId = companyGroup.parent_group;
                      const rootGroupName = companyGroup.parent_group_name;
                      // No parent → the home group is already the top for this
                      // user; the existing home-group entry below covers it.
                      if (!rootGroupId || !rootGroupName) return null;
                      const sourceList = hasSiteRestrictions
                        ? switcherSites.map(s => s.group_id)
                        : switcherCompanies.map(c => (c as unknown as Record<string, unknown>)['group'] as number | undefined);
                      const distinctDivisionIds = new Set<number>();
                      for (const gid of sourceList) {
                        if (gid) distinctDivisionIds.add(gid as number);
                      }
                      // Only when the user actually spans multiple divisions.
                      if (distinctDivisionIds.size <= 1) return null;
                      const isRootSelected = viewLevel === 'GROUP' && selectedGroupId === rootGroupId;
                      return (
                        <DropdownMenuItem
                          onClick={() => selectGroup(rootGroupId, rootGroupName)}
                          className={cn(
                            "cursor-pointer",
                            isRootSelected && "bg-primary/15 text-primary"
                          )}
                        >
                          <Layers className={cn("mr-2 h-4 w-4", isRootSelected ? "text-primary" : "text-purple-500")} />
                          <div className="flex-1 min-w-0">
                            <span className={cn("block whitespace-normal break-words font-medium", isRootSelected && "font-semibold")}>
                              {rootGroupName}
                            </span>
                          </div>
                          {isRootSelected && <Check className="h-4 w-4 text-primary ml-1" />}
                        </DropdownMenuItem>
                      );
                    })()}

                    {/* CR-4541 / CR-5315: Home-group rollup entry. Skip when
                        the home group will already render as one of the
                        division headers below (dedup) — the division header
                        for the same groupId selects the same rollup. Keep it
                        when the home group is synthetic (super-admin
                        "All Companies", id=0) or otherwise not in the
                        divisions list, because in those cases the top-level
                        entry is the only way to pick the group rollup.

                        CR-5374 r3: the dedup check used to fire whenever any
                        accessible site/company shared the home group id —
                        but when the lower hierarchy collapses to a flat list
                        (single division, no ungrouped entries) the division
                        header never renders, leaving site-restricted users
                        with no way to pick the Division-level rollup. Only
                        skip when the lower hierarchy will actually emit the
                        division header. */}
                    {(() => {
                      if (!companyGroup) return null;
                      const visible = (!hasSiteRestrictions && switcherCompanies.length > 1)
                        || (hasSiteRestrictions && switcherSites.length > 1);
                      if (!visible) return null;
                      // Will the home group already appear as a division header?
                      // The home-group rollup is pinned here as the one
                      // "everything" entry; the division list below skips the
                      // home group so it never doubles as a mid-list header.
                      return (
                        <DropdownMenuItem
                          onClick={() => selectGroup(companyGroup.id, companyGroup.name)}
                          className={cn(
                            "cursor-pointer",
                            viewLevel === 'GROUP' && selectedGroupId === companyGroup.id && "bg-primary/15 text-primary"
                          )}
                        >
                          <Layers className={cn("mr-2 h-4 w-4", viewLevel === 'GROUP' && selectedGroupId === companyGroup.id ? "text-primary" : "text-purple-500")} />
                          <div className="flex-1 min-w-0">
                            <span className={cn("block whitespace-normal break-words font-medium", viewLevel === 'GROUP' && selectedGroupId === companyGroup.id && "font-semibold")}>
                              {companyGroup.name}
                            </span>
                            <span className="block text-xs text-muted-foreground">
                              All regions &amp; divisions
                            </span>
                          </div>
                          {viewLevel === 'GROUP' && selectedGroupId === companyGroup.id && <Check className="h-4 w-4 text-primary ml-1" />}
                        </DropdownMenuItem>
                      );
                    })()}

                    {/* CR-4541: Divisions (sub-groups) with nested sites */}
                    {/* Group companies by their group (division) and show as a hierarchy */}
                    {!hasSiteRestrictions && (() => {
                      // Build division map: group companies by their group_name
                      const divisions = new Map<string, typeof switcherCompanies>();
                      const ungrouped: typeof switcherCompanies = [];
                      for (const company of switcherCompanies) {
                        const groupName = (company as unknown as Record<string, unknown>)['group_name'] as string | undefined;
                        const groupId = (company as unknown as Record<string, unknown>)['group'] as number | undefined;
                        const gidNum = typeof groupId === 'object' && groupId !== null
                          ? (groupId as { id?: number }).id
                          : groupId;
                        if (groupName && groupId && companyGroup
                            && (gidNum === companyGroup.id || groupName === companyGroup.name)) {
                          // Direct members of the home group list flat — the
                          // pinned rollup above already covers the group level.
                          ungrouped.push(company);
                        } else if (groupName && groupId) {
                          const key = `${groupId}:${groupName}`;
                          if (!divisions.has(key)) divisions.set(key, []);
                          divisions.get(key)!.push(company);
                        } else {
                          ungrouped.push(company);
                        }
                      }

                      // A single real division used to collapse to the flat
                      // branch, which erased the only clickable group entry
                      // for a tenant whose group has one onboarded company so
                      // far (Atlas Copco: the group exists, UUM joins later).
                      // It now renders the hierarchy like any other division;
                      // a platform admin gets the same header, built from the
                      // groups of the companies it can see, so no super-admin
                      // special case is needed. (This flat branch is thereby
                      // reachable only with an empty company list; ungrouped
                      // companies render via the hierarchy branch's ungrouped
                      // section, as they already did.)
                      if (divisions.size === 0 && ungrouped.length === 0) {
                        return switcherCompanies.map((company) => {
                          const isSelected = viewLevel === 'COMPANY' && company.name === selectedCompanyName;
                          return (
                            <DropdownMenuItem
                              key={company.id}
                              onClick={() => selectCompany(company.id, company.name)}
                              className={cn(
                                "cursor-pointer pl-6",
                                isSelected && "bg-primary/15 text-primary"
                              )}
                            >
                              <Building2 className={cn("mr-2 h-4 w-4", isSelected ? "text-primary" : "text-blue-500")} />
                              <span className={cn("flex-1 whitespace-normal break-words", isSelected && "font-semibold")}>
                                {company.name}
                              </span>
                              {isSelected && <Check className="h-4 w-4 text-primary ml-1" />}
                            </DropdownMenuItem>
                          );
                        });
                      }

                      // Multiple divisions: render hierarchy (Division → Sites)
                      return (
                        <>
                          {Array.from(divisions.entries()).map(([key, companies]) => {
                            const divisionName = key.split(':').slice(1).join(':');
                            return (
                              <div key={key}>
                                {/* Division header — clickable to select division-level rollup */}
                                {(() => {
                                  const groupId = parseInt(key.split(':')[0], 10);
                                  const isDivisionSelected = viewLevel === 'GROUP' && selectedGroupId === groupId;
                                  return (
                                    <DropdownMenuItem
                                      onClick={() => { if (groupId) selectGroup(groupId, divisionName); }}
                                      className={cn("cursor-pointer pl-6", isDivisionSelected && "bg-primary/15 text-primary")}
                                    >
                                      <Building2 className={cn("mr-2 h-4 w-4", isDivisionSelected ? "text-primary" : "text-indigo-500")} />
                                      <span className={cn("text-xs font-semibold uppercase tracking-wide", isDivisionSelected && "text-primary")}>
                                        {divisionName}
                                      </span>
                                      {isDivisionSelected && <Check className="h-4 w-4 text-primary ml-1" />}
                                    </DropdownMenuItem>
                                  );
                                })()}
                                {/* Sites within this division */}
                                {companies.map((company) => {
                                  const isSelected = viewLevel === 'COMPANY' && company.name === selectedCompanyName;
                                  return (
                                    <DropdownMenuItem
                                      key={company.id}
                                      onClick={() => selectCompany(company.id, company.name)}
                                      className={cn(
                                        "cursor-pointer pl-10",
                                        isSelected && "bg-primary/15 text-primary"
                                      )}
                                    >
                                      <Building2 className={cn("mr-2 h-4 w-4", isSelected ? "text-primary" : "text-blue-500")} />
                                      <span className={cn("flex-1 whitespace-normal break-words", isSelected && "font-semibold")}>
                                        {company.name}
                                      </span>
                                      {isSelected && <Check className="h-4 w-4 text-primary ml-1" />}
                                    </DropdownMenuItem>
                                  );
                                })}
                              </div>
                            );
                          })}
                          {/* Ungrouped companies */}
                          {ungrouped.map((company) => {
                            const isSelected = viewLevel === 'COMPANY' && company.name === selectedCompanyName;
                            return (
                              <DropdownMenuItem
                                key={company.id}
                                onClick={() => selectCompany(company.id, company.name)}
                                className={cn(
                                  "cursor-pointer pl-6",
                                  isSelected && "bg-primary/15 text-primary"
                                )}
                              >
                                <Building2 className={cn("mr-2 h-4 w-4", isSelected ? "text-primary" : "text-blue-500")} />
                                <span className={cn("flex-1 whitespace-normal break-words", isSelected && "font-semibold")}>
                                  {company.name}
                                </span>
                                {isSelected && <Check className="h-4 w-4 text-primary ml-1" />}
                              </DropdownMenuItem>
                            );
                          })}
                        </>
                      );
                    })()}

                    {/* Site-restricted users (one CustomUser linked via
                        UserSitePermission to multiple sites across several
                        groups). Group the sites by their parent CompanyGroup
                        so each parent surfaces as a clickable division header
                        with its sites nested below — same shape as the
                        switcherCompanies hierarchy above. */}
                    {hasSiteRestrictions && (() => {
                      const siteDivisions = new Map<string, typeof switcherSites>();
                      const siteUngrouped: typeof switcherSites = [];
                      for (const site of switcherSites) {
                        const groupName = site.group_name;
                        const groupId = site.group_id;
                        if (groupName && groupId && companyGroup
                            && (groupId === companyGroup.id || groupName === companyGroup.name)) {
                          // Direct members of the home group list flat — the
                          // pinned rollup above already covers the group level,
                          // so a second header would double the checkmark.
                          siteUngrouped.push(site);
                        } else if (groupName && groupId) {
                          const key = `${groupId}:${groupName}`;
                          if (!siteDivisions.has(key)) siteDivisions.set(key, []);
                          siteDivisions.get(key)!.push(site);
                        } else {
                          siteUngrouped.push(site);
                        }
                      }

                      // No usable grouping → render flat (single CompanyGroup or none)
                      if (siteDivisions.size <= 1 && siteUngrouped.length === 0) {
                        return switcherSites.map((site) => {
                          const isSelected = viewLevel === 'SITE' && site.name === selectedSiteName;
                          return (
                            <DropdownMenuItem
                              key={site.id}
                              onClick={() => selectSite(site.id, site.name)}
                              className={cn(
                                "cursor-pointer pl-6",
                                isSelected && "bg-primary/15 text-primary"
                              )}
                            >
                              <Building2 className={cn("mr-2 h-4 w-4", isSelected ? "text-primary" : "text-blue-500")} />
                              <span className={cn("flex-1 whitespace-normal break-words", isSelected && "font-semibold")}>
                                {site.name}
                              </span>
                              {isSelected && <Check className="h-4 w-4 text-primary ml-1" />}
                            </DropdownMenuItem>
                          );
                        });
                      }

                      return (
                        <>
                          {Array.from(siteDivisions.entries()).map(([key, sites]) => {
                            const divisionName = key.split(':').slice(1).join(':');
                            const groupId = parseInt(key.split(':')[0], 10);
                            const isDivisionSelected = viewLevel === 'GROUP' && selectedGroupId === groupId;
                            return (
                              <div key={key}>
                                <DropdownMenuItem
                                  onClick={() => { if (groupId) selectGroup(groupId, divisionName); }}
                                  className={cn("cursor-pointer pl-6", isDivisionSelected && "bg-primary/15 text-primary")}
                                >
                                  <Building2 className={cn("mr-2 h-4 w-4", isDivisionSelected ? "text-primary" : "text-indigo-500")} />
                                  <span className={cn("text-xs font-semibold uppercase tracking-wide", isDivisionSelected && "text-primary")}>
                                    {divisionName}
                                  </span>
                                  {isDivisionSelected && <Check className="h-4 w-4 text-primary ml-1" />}
                                </DropdownMenuItem>
                                {sites.map((site) => {
                                  const isSelected = viewLevel === 'SITE' && site.name === selectedSiteName;
                                  return (
                                    <DropdownMenuItem
                                      key={site.id}
                                      onClick={() => selectSite(site.id, site.name)}
                                      className={cn(
                                        "cursor-pointer pl-10",
                                        isSelected && "bg-primary/15 text-primary"
                                      )}
                                    >
                                      <Building2 className={cn("mr-2 h-4 w-4", isSelected ? "text-primary" : "text-blue-500")} />
                                      <span className={cn("flex-1 whitespace-normal break-words", isSelected && "font-semibold")}>
                                        {site.name}
                                      </span>
                                      {isSelected && <Check className="h-4 w-4 text-primary ml-1" />}
                                    </DropdownMenuItem>
                                  );
                                })}
                              </div>
                            );
                          })}
                          {siteUngrouped.map((site) => {
                            const isSelected = viewLevel === 'SITE' && site.name === selectedSiteName;
                            return (
                              <DropdownMenuItem
                                key={site.id}
                                onClick={() => selectSite(site.id, site.name)}
                                className={cn(
                                  "cursor-pointer pl-6",
                                  isSelected && "bg-primary/15 text-primary"
                                )}
                              >
                                <Building2 className={cn("mr-2 h-4 w-4", isSelected ? "text-primary" : "text-blue-500")} />
                                <span className={cn("flex-1 whitespace-normal break-words", isSelected && "font-semibold")}>
                                  {site.name}
                                </span>
                                {isSelected && <Check className="h-4 w-4 text-primary ml-1" />}
                              </DropdownMenuItem>
                            );
                          })}
                        </>
                      );
                    })()}
                    </div>
                  </>
                )}

                {/* Portal Mode Toggle (Customer/Supplier) */}
                {supportsDualMode && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={togglePortalMode} className="cursor-pointer">
                      <ArrowRightLeft className="mr-2 h-4 w-4 text-muted-foreground" />
                      <span>Switch to {portalMode === 'customer' ? 'Supplier' : 'Customer'} View</span>
                    </DropdownMenuItem>
                  </>
                )}

                <DropdownMenuSeparator />
                <DropdownMenuItem asChild>
                  <Link to="/settings" className="flex items-center cursor-pointer">
                    <Settings className="mr-2 h-4 w-4 text-gray-500 dark:text-muted-foreground" aria-hidden="true" />
                    My Account
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} className="flex items-center text-red-600 dark:text-destructive focus:text-red-600 dark:focus:text-destructive focus:bg-red-50 dark:focus:bg-destructive/10 cursor-pointer">
                  <LogOut className="mr-2 h-4 w-4" aria-hidden="true" />
                  Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>
    </header>
  );
}
